import React from 'react';
import { Typography, Alert, Tabs, Button, Space } from 'antd';

const { Text } = Typography;

interface ProteinMPNNResultsProps {
  taskResult: any;
  data?: any;
  title?: string; // 自定义标题
  showTitle?: boolean; // 是否显示标题
}

const ProteinMPNNResults: React.FC<ProteinMPNNResultsProps> = ({ 
  taskResult, 
  data, 
  title = "ProteinMPNN 设计结果", 
  showTitle = true 
}) => {
  
  // 调试信息
  console.log('🔍 ProteinMPNNResults Debug:');
  console.log('taskResult:', taskResult);
  console.log('taskResult.result:', taskResult?.result);
  console.log('taskResult.result.results:', taskResult?.result?.results);
  
  // 检查是否有FASTA结果 - 兼容多种数据格式
  const hasFastaResults = (res: any): boolean => {
    if (!res) return false;
    
    // 格式1: Profile页面格式
    if (res.result?.results && Array.isArray(res.result.results) && res.result.results.length > 0) {
      const fastaContents = res.result.results[0]?.result?.fasta_contents;
      if (Boolean(fastaContents && Array.isArray(fastaContents) && fastaContents.length > 0)) {
        return true;
      }
    }
    
    // 格式2: 前端页面格式
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      const fastaContents = res.results[0]?.result?.fasta_contents;
      return Boolean(fastaContents && Array.isArray(fastaContents) && fastaContents.length > 0);
    }
    
    return false;
  };
  
  // 获取所有FASTA文件内容 - 兼容多种数据格式
  const getAllFastaContents = (res: any): Array<{filename: string, content: string}> => {
    if (!res) return [];
    
    // 格式1: Profile页面格式 - res.result.results[0].result.fasta_contents
    if (res.result?.results && Array.isArray(res.result.results) && res.result.results.length > 0) {
      const fastaContents = res.result.results[0]?.result?.fasta_contents;
      console.log('找到 Profile格式 fasta_contents:', fastaContents);
      if (fastaContents && Array.isArray(fastaContents)) {
        return fastaContents;
      }
    }
    
    // 格式2: 前端页面格式 - res.results[0].result.fasta_contents
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      const fastaContents = res.results[0]?.result?.fasta_contents;
      console.log('找到 前端页面格式 fasta_contents:', fastaContents);
      if (fastaContents && Array.isArray(fastaContents)) {
        return fastaContents;
      }
    }
    
    return [];
  };
  
  const fastaContents = getAllFastaContents(taskResult);
  
  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }
  
  if (!hasFastaResults(taskResult) || fastaContents.length === 0) {
    return (
      <Alert
        type="info"
        message="无ProteinMPNN序列结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }
  
  const tabItems = fastaContents.map((fasta, index) => ({
    key: index.toString(),
    label: `序列 ${index + 1}`,
    children: (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>文件名:</Text>
            <Text code>{fasta.filename}</Text>
            <Button 
              size="small" 
              onClick={() => {
                const blob = new Blob([fasta.content], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fasta.filename;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              下载
            </Button>
          </Space>
        </div>
        <div style={{ 
          background: '#f5f5f5', 
          padding: 16, 
          borderRadius: 4, 
          fontFamily: 'monospace', 
          wordBreak: 'break-all',
          maxHeight: 400,
          overflow: 'auto'
        }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {fasta.content}
          </pre>
        </div>
      </div>
    )
  }));
  
  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({fastaContents.length} 个序列文件)</Text>
        </div>
      )}
      <Tabs 
        defaultActiveKey="0" 
        items={tabItems}
        size="small"
      />
    </div>
  );
};

export default ProteinMPNNResults;
